import { useMemo, useState } from 'react';
import { ResultsTab } from '../types';
import { useLeaderboardParticipants } from '../../../hooks/useLeaderboardParticipants';
import { useLeaderboardDistricts } from '../../../hooks/useLeaderboardDistricts';
import { useLeaderboardOffices } from '../../../hooks/useLeaderboardOffices';

export const useResultsContent = (eventId?: number) => {
  const [activeTab, setActiveTab] = useState<ResultsTab>('participants');
  const {
    participants,
    myParticipant,
    pageSize,
    totalPages: participantsTotalPages,
    pageNumber: participantsPage,
    setPageNumber: setParticipantsPage,
    isLoading: participantsLoading,
  } = useLeaderboardParticipants(eventId, undefined, null, null);
  const {
    districts,
    totalPages: districtsTotalPages,
    pageNumber: districtsPage,
    setPageNumber: setDistrictsPage,
    isLoading: districtsLoading,
  } = useLeaderboardDistricts(eventId, undefined, null, null);
  const {
    offices,
    totalPages: officesTotalPages,
    pageNumber: officesPage,
    setPageNumber: setOfficesPage,
    isLoading: officesLoading,
  } = useLeaderboardOffices(eventId, undefined, null, null);

  const participantRows = useMemo(
    () =>
      (participants || []).map((p) => ({
        id: p.id,
        rank: p.rank,
        participant: `${p.firstName} ${p.lastName}`.trim(),
        area: p.districtNames || '',
      })),
    [participants]
  );

  const areaRows = useMemo(
    () =>
      (districts || []).map((d) => ({
        id: d.id,
        rank: d.rank,
        area: d.districtName,
        score: d.totalScore,
      })),
    [districts]
  );

  const officeRows = useMemo(
    () =>
      (offices || []).map((o) => ({
        id: o.id,
        rank: o.rank,
        office: o.name,
        score: o.totalScore,
      })),
    [offices]
  );

  const currentData = useMemo(() => {
    if (activeTab === 'participants') {
      let baseRows = participantRows;

      if (!myParticipant) return baseRows;

      const presentInApiPage = (participants || []).some((p) => p.id === myParticipant.id);
      if (presentInApiPage) return baseRows;

      const myPage = Math.max(1, Math.ceil(myParticipant.rank / (pageSize || 10)));
      const myRow = {
        id: myParticipant.id,
        rank: myParticipant.rank,
        participant: `${myParticipant.firstName} ${myParticipant.lastName}`.trim(),
        area: myParticipant.districtNames || '',
      };
      if (myPage > participantsPage) return [...baseRows, myRow];
      if (myPage < participantsPage) return [myRow, ...baseRows];
      return [...baseRows, myRow];
    }
    if (activeTab === 'areas') return areaRows;
    return officeRows;
  }, [
    activeTab,
    participantRows,
    participantsPage,
    areaRows,
    officeRows,
    myParticipant,
    participants,
    pageSize,
  ]);

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId as ResultsTab);
    setParticipantsPage(1);
    setDistrictsPage(1);
    setOfficesPage(1);
  };

  const handlePageChange = (page: number) => {
    if (activeTab === 'participants') setParticipantsPage(page);
    else if (activeTab === 'areas') setDistrictsPage(page);
    else setOfficesPage(page);
  };

  const totalPages =
    activeTab === 'participants'
      ? participantsTotalPages
      : activeTab === 'areas'
        ? districtsTotalPages
        : officesTotalPages;
  const currentPage =
    activeTab === 'participants'
      ? participantsPage
      : activeTab === 'areas'
        ? districtsPage
        : officesPage;

  const isLoading = useMemo(() => {
    if (activeTab === 'participants') return participantsLoading;
    if (activeTab === 'areas') return districtsLoading;
    if (activeTab === 'offices') return officesLoading;
    return false;
  }, [activeTab, participantsLoading, districtsLoading, officesLoading]);

  return {
    activeTab,
    currentData,
    totalPages,
    currentPage,
    myParticipant,
    isLoading,
    handleTabChange,
    handlePageChange,
  };
};
